import {
  PLUGIN_FONT_SIZE_RANGE,
  PLUGIN_FOOTER_LINK_MAX,
  PLUGIN_FOOTER_TEXT_MAX,
  PLUGIN_LETTER_SPACING_RANGE,
  PLUGIN_SPACING_UNIT_RANGE,
} from '../constants/embedOptions'
import {
  PLUGIN_CONTROL_CHARACTERS,
  PLUGIN_ENTITY_ID_MAX,
  PLUGIN_ENTITY_ID_PATTERN,
  PLUGIN_FONT_NAME_PATTERN,
  PLUGIN_HEX_COLOUR_PATTERN,
  PLUGIN_VALIDATION_MESSAGES as MESSAGES,
} from '../constants/embedValidation'
import type {
  PluginEmbedSettings,
  PluginFooter,
  PluginSpacing,
} from '../types/embedSettings'

type Errors = Record<string, string>

const MULTIPLE_EPSILON = 1e-9

/** Float-safe: 0.3 is a multiple of 0.1, 0.15 is not. */
const isMultipleOf = (value: number, step: number): boolean => {
  const ratio = value / step
  return Math.abs(ratio - Math.round(ratio)) < MULTIPLE_EPSILON
}

const isInRange = (value: number, min: number, max: number): boolean =>
  Number.isFinite(value) && value >= min && value <= max

const isEntityId = (value: string): boolean =>
  value.length <= PLUGIN_ENTITY_ID_MAX && PLUGIN_ENTITY_ID_PATTERN.test(value)

const isHttpsLink = (value: string): boolean => {
  try {
    const url = new URL(value)
    return url.protocol === 'https:' && !url.username && !url.password
  } catch {
    return false
  }
}

const isPositiveWhole = (value: number): boolean =>
  Number.isInteger(value) && value > 0

const isValidLetterSpacing = (value: number): boolean =>
  isInRange(
    value,
    PLUGIN_LETTER_SPACING_RANGE.min,
    PLUGIN_LETTER_SPACING_RANGE.max
  ) && isMultipleOf(value, PLUGIN_LETTER_SPACING_RANGE.step)

const isValidSpacing = (value: PluginSpacing): boolean =>
  typeof value === 'string' ||
  (isInRange(
    value,
    PLUGIN_SPACING_UNIT_RANGE.min,
    PLUGIN_SPACING_UNIT_RANGE.max
  ) &&
    isMultipleOf(value, PLUGIN_SPACING_UNIT_RANGE.step))

const entityIdErrors = (entityId: string): Errors => {
  if (entityId === '') {
    return { entityId: MESSAGES.required }
  }
  return isEntityId(entityId) ? {} : { entityId: MESSAGES.entityId }
}

const brandingErrors = (branding: PluginEmbedSettings['branding']): Errors => {
  const errors: Errors = {}
  const {
    fontFamily,
    headingFontFamily,
    fontSize,
    letterSpacing,
    headingLetterSpacing,
    spacing,
  } = branding
  if (fontFamily !== undefined && !PLUGIN_FONT_NAME_PATTERN.test(fontFamily)) {
    errors['branding.fontFamily'] = MESSAGES.fontName
  }
  if (
    headingFontFamily !== undefined &&
    !PLUGIN_FONT_NAME_PATTERN.test(headingFontFamily)
  ) {
    errors['branding.headingFontFamily'] = MESSAGES.fontName
  }
  if (
    fontSize !== undefined &&
    !(
      Number.isInteger(fontSize) &&
      isInRange(
        fontSize,
        PLUGIN_FONT_SIZE_RANGE.min,
        PLUGIN_FONT_SIZE_RANGE.max
      )
    )
  ) {
    errors['branding.fontSize'] = MESSAGES.fontSize
  }
  if (letterSpacing !== undefined && !isValidLetterSpacing(letterSpacing)) {
    errors['branding.letterSpacing'] = MESSAGES.letterSpacing
  }
  if (
    headingLetterSpacing !== undefined &&
    !isValidLetterSpacing(headingLetterSpacing)
  ) {
    errors['branding.headingLetterSpacing'] = MESSAGES.letterSpacing
  }
  if (spacing !== undefined && !isValidSpacing(spacing)) {
    errors['branding.spacing'] = MESSAGES.spacing
  }
  return errors
}

const footerErrors = (footer: PluginFooter | null): Errors => {
  if (footer === null || typeof footer === 'string') {
    return {}
  }
  const errors: Errors = {}
  const text = footer.text.trim()
  if (text === '') {
    errors['display.footer.text'] = MESSAGES.footerTextRequired
  } else if (text.length > PLUGIN_FOOTER_TEXT_MAX) {
    errors['display.footer.text'] = MESSAGES.footerTextMax
  } else if (PLUGIN_CONTROL_CHARACTERS.test(text)) {
    errors['display.footer.text'] = MESSAGES.footerTextPlain
  }

  const link = footer.link?.trim() ?? ''
  if (link === '') {
    return errors
  }
  if (link.length > PLUGIN_FOOTER_LINK_MAX) {
    errors['display.footer.link'] = MESSAGES.footerLinkMax
  } else if (!isHttpsLink(link)) {
    errors['display.footer.link'] = MESSAGES.footerLink
  }
  return errors
}

const displayErrors = (display: PluginEmbedSettings['display']): Errors => {
  const errors: Errors = {}
  if (display.maxHeight !== null && !isPositiveWhole(display.maxHeight)) {
    errors['display.maxHeight'] = MESSAGES.positiveWhole
  }
  if (display.maxWidth !== null && !isPositiveWhole(display.maxWidth)) {
    errors['display.maxWidth'] = MESSAGES.positiveWhole
  }
  if (display.feedOrgId !== null && !isEntityId(display.feedOrgId)) {
    errors['display.feedOrgId'] = MESSAGES.entityId
  }
  return { ...errors, ...footerErrors(display.footer) }
}

const colourErrors = (colours: PluginEmbedSettings['colours']): Errors => {
  const errors: Errors = {}
  for (const [name, value] of Object.entries(colours)) {
    if (value !== undefined && !PLUGIN_HEX_COLOUR_PATTERN.test(value)) {
      errors[`colours.${name}`] = MESSAGES.hexColour
    }
  }
  return errors
}

/** The message for each invalid field, keyed by Formik path. Unset options pass. */
export const getPluginSettingsErrors = (
  settings: PluginEmbedSettings
): Errors => ({
  ...entityIdErrors(settings.entityId),
  ...brandingErrors(settings.branding),
  ...displayErrors(settings.display),
  ...colourErrors(settings.colours),
})
